import type { SerializedConstructor } from '@langchain/core/load/serializable';
import { PlusIcon } from '@langchain/untitled-ui-icons';
import { Button, FormLabel } from '@mui/joy';

import { useMemo, useState } from 'react';

import { EditInputFunction } from '@/components/ExampleCrudPane/EditInputFunctions';
import {
  AnthropicTool,
  OpenAIFunction,
  emptyAnthropicTool,
  isAnthropicTools,
} from '@/utils/ai-tools';
import { cn } from '@/utils/tailwind';

import { ManifestFunctionViewer } from '../../../tools/ToolEditors';
import {
  ManifestEditorProps,
  tableVariantLabelClass,
} from '../../Manifest.utils';

function ManifestChatAnthropicToolEditor(props: {
  value: AnthropicTool;
  onSubmit: (value: AnthropicTool) => void;
  onCancel: () => void;
  onRemove: () => void;
  readOnly?: boolean;
}) {
  const [value, setValue] = useState(props.value);

  const oaiValue = useMemo((): OpenAIFunction => {
    if (!value) return value;
    return {
      name: value.name,
      description: value.description,
      parameters: value.input_schema,
    };
  }, [value]);

  const onOAIChange = (value: Partial<OpenAIFunction>) => {
    setValue({
      name: value.name ?? '',
      description: value.description,
      input_schema: value.parameters,
    });
  };

  return (
    <EditInputFunction
      value={oaiValue}
      onChange={onOAIChange}
      onRemove={props.onRemove}
      onCancel={props.onCancel}
      onSubmit={() => props.onSubmit?.(value)}
      readOnly={props.readOnly}
    />
  );
}

function ManifestChatAnthropicTool(props: {
  value: AnthropicTool;
  onChange: (value: AnthropicTool) => void;
  onRemove: () => void;
  readOnly?: boolean;
}) {
  const fn = props.value;
  const [open, setOpen] = useState(!fn.name);

  if (!open) {
    return (
      <ManifestFunctionViewer
        onOpen={() => setOpen(true)}
        onRemove={props.onRemove}
        name={props.value.name}
        defaultName="Untitled tool"
        readOnly={props.readOnly}
      />
    );
  }

  return (
    <ManifestChatAnthropicToolEditor
      value={fn}
      onRemove={props.onRemove}
      onCancel={() => setOpen(false)}
      onSubmit={(value) => {
        props.onChange(value);
        setOpen(false);
      }}
      readOnly={props.readOnly}
    />
  );
}

export function ManifestChatAnthropicToolListEditor(
  props: ManifestEditorProps<SerializedConstructor> & {
    hideTitle?: boolean;
  }
) {
  const tools = isAnthropicTools(props.options.tools)
    ? props.options.tools
    : [];

  function handleChange(tools: Array<AnthropicTool> | null) {
    props.onOptionsChange({
      ...props.options,
      tools: tools?.length ? tools : undefined,
    });
  }

  if (props.readOnly && tools.length === 0) {
    return <></>;
  }

  return (
    <div className="flex flex-col gap-2">
      {!props.hideTitle &&
        (props.readOnly && props.variant === 'table' ? (
          <div className={cn(tableVariantLabelClass, 'pt-6')}>Tools</div>
        ) : (
          <FormLabel>Tools</FormLabel>
        ))}

      {tools.map((value, index) => {
        return (
          <ManifestChatAnthropicTool
            key={index}
            value={value}
            onChange={(item) => {
              handleChange(tools.map((v, i) => (i === index ? item : v)));
            }}
            onRemove={() => {
              handleChange(tools.filter((_, i) => i !== index));
            }}
            readOnly={props.readOnly}
          />
        );
      })}

      {!props.readOnly && (
        <Button
          variant="outlined"
          sx={(theme) => ({ bg: theme.palette.neutral.solidBg })}
          className="self-start"
          color="neutral"
          size="sm"
          onClick={() => handleChange([...tools, emptyAnthropicTool()])}
          startDecorator={<PlusIcon className="size-4" />}
        >
          Add Tool
        </Button>
      )}
    </div>
  );
}
